/** PDF files opened as read-only preview (not edited as text). */

function basenameLower(pathOrName: string): string {
  const s = pathOrName.trim().replace(/\\/g, "/");
  const i = s.lastIndexOf("/");
  return (i >= 0 ? s.slice(i + 1) : s).toLowerCase();
}

/**
 * True when `file` should open as PDF preview (not UTF-8 text).
 * @param pathOrNameHint Optional path or title (e.g. workspace `root/dir/file.pdf`). If its basename
 *   ends with `.pdf`, we treat it as PDF regardless of `File.type` / `File.name` from `getFile()`
 *   (e.g. `application/force-download` and name `download`).
 */
export function isPdfViewFile(file: File, pathOrNameHint?: string | null): boolean {
  const t = file.type.trim().toLowerCase();
  if (t === "application/pdf") return true;

  const name = file.name.trim().toLowerCase();
  if (name.endsWith(".pdf")) return true;

  const hint = pathOrNameHint?.trim() ? basenameLower(pathOrNameHint) : "";
  // Workspace / MRU / tab path is authoritative: browsers often set unrelated MIME types
  // (e.g. application/force-download) and generic File.name ("download") for getFile().
  if (hint.endsWith(".pdf")) return true;

  return false;
}

/**
 * File contains the ASCII `%PDF` header within the first bytes (ISO 32000 allows leading
 * binary comments; some tools prepend a UTF-8 BOM before `%PDF`).
 */
export async function fileHasPdfSignature(file: File): Promise<boolean> {
  if (file.size < 4) return false;
  const maxScan = Math.min(2048, file.size);
  const buf = new Uint8Array(await file.slice(0, maxScan).arrayBuffer());
  for (let i = 0; i <= buf.length - 4; i++) {
    if (buf[i] === 0x25 && buf[i + 1] === 0x50 && buf[i + 2] === 0x44 && buf[i + 3] === 0x46) return true;
  }
  return false;
}

/**
 * Metadata and/or `%PDF` header (covers wrong `getFile().name` / `type`).
 * @param handleNameHint Optional `FileSystemFileHandle.name` when the display path is wrong.
 */
export async function shouldTreatFileAsPdf(
  file: File,
  pathHint?: string | null,
  handleNameHint?: string | null,
): Promise<boolean> {
  if (isPdfViewFile(file, pathHint) || isPdfViewFile(file, handleNameHint)) return true;
  return fileHasPdfSignature(file);
}
